import { useState, useEffect, useRef, RefObject } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { editPhotoActions } from '../../../reducers/editPhoto'
import { selectBeforeImageData } from '../../../selectors'

interface UseStageSetupReturn {
    stageWrapRef: RefObject<HTMLDivElement>
    stageRef: RefObject<any>
    outputStageRef: RefObject<any>
    previewCanvasRef: RefObject<HTMLCanvasElement>
    stageWidth: number
    stageHeight: number
    outputStageWidth: number
    outputStageHeight: number
}

/**
 * Stage, OutputStage, Canvas の初期化とサイズ管理を行うカスタムHook
 */
export function useStageSetup(): UseStageSetupReturn {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
    const stageWrapRef = useRef<HTMLDivElement>(null)
    const stageRef = useRef<any>(null)
    const outputStageRef = useRef<any>(null)
    const previewCanvasRef = useRef<HTMLCanvasElement>(null)

    const [stageWidth, setStageWidth] = useState(0)
    const [stageHeight, setStageHeight] = useState(0)
    const [outputStageWidth, setOutputStageWidth] = useState(0)
    const [outputStageHeight, setOutputStageHeight] = useState(0)

    const { stage, outputStage, previewCanvas } = useSelector(
        (state: any) => state.editPhoto
    )
    const { afterImageRef } = useSelector(
        (state: any) => state.roomDesign
    )
    const selectedBeforeImageData = useSelector((state: any) => selectBeforeImageData(state))

    // Stage, OutputStage, Canvas の初期化（マウント時のみ実行）
    useEffect(() => {
        const newStage = stageRef.current?.getStage()
        if (newStage) {
            dispatch(editPhotoActions.setStage(newStage))
        }

        const newOutputStage = outputStageRef.current?.getStage()
        if (newOutputStage) {
            dispatch(editPhotoActions.setOutputStage(newOutputStage))
        }

        const newPreviewCanvas = previewCanvasRef.current
        if (newPreviewCanvas) {
            dispatch(editPhotoActions.setPreviewCanvas(newPreviewCanvas))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ステージサイズの計算と設定
    useEffect(() => {
        if (!stageWrapRef.current || !afterImageRef) return

        const maxStageHeight =
            window.innerHeight - 11 * parseInt(getComputedStyle(document.documentElement).fontSize)
        const maxStageWidth = stageWrapRef.current.clientWidth

        let newStageHeight = afterImageRef.height * (maxStageWidth / afterImageRef.width)
        let newStageWidth: number

        if (newStageHeight <= maxStageHeight) {
            newStageWidth = maxStageWidth
        } else {
            newStageHeight = maxStageHeight
            newStageWidth = afterImageRef.width * (maxStageHeight / afterImageRef.height)
        }

        setStageHeight(newStageHeight)
        setStageWidth(newStageWidth)
        stageWrapRef.current.style.height = newStageHeight + 'px'

        // 出力ステージのサイズ（元画像のサイズ）
        const outputWidth = afterImageRef.naturalWidth || afterImageRef.width
        const outputHeight = afterImageRef.naturalHeight || afterImageRef.height
        setOutputStageWidth(outputWidth)
        setOutputStageHeight(outputHeight)
    }, [selectedBeforeImageData, afterImageRef])

    return {
        stageWrapRef,
        stageRef,
        outputStageRef,
        previewCanvasRef,
        stageWidth,
        stageHeight,
        outputStageWidth,
        outputStageHeight,
    }
}
